/**
 * Column statistics, computed as SQL against the browser-local cache so the
 * Column Explorer is instant (spec §4.7). Nothing here touches the network.
 */

import { classifyType, isNumericClass, quoteIdent, renderableText, toCount, toFiniteNumber } from '@/lib/values';
import type { ColumnSchema, DuckDbType } from '@/types/api';
import type { LocalEngine } from './types';
import { sqlString } from './sqlgen';

export const HISTOGRAM_BINS = 24;
export const TOP_VALUES_LIMIT = 12;
/** At or below this many distinct values, a column is shown as top-N bars. */
export const LOW_CARDINALITY_THRESHOLD = 25;

export type DistributionKind = 'histogram' | 'timeseries' | 'categories' | 'boolean' | 'none';

export interface HistogramBin {
  readonly index: number;
  readonly lo: number;
  readonly hi: number;
  readonly count: number;
  /** Human-readable bucket range, used in the detail pane and tooltips. */
  readonly label: string;
}

export interface TopValue {
  readonly value: string | null;
  /** Distinguishes SQL NULL from the string "NULL" and from an empty string. */
  readonly isNull: boolean;
  readonly isEmpty: boolean;
  readonly count: number;
  readonly fraction: number;
}

export interface ColumnProfile {
  readonly column: string;
  readonly type: DuckDbType;
  readonly kind: DistributionKind;
  readonly total: number;
  readonly nonNull: number;
  readonly nullCount: number;
  readonly nullFraction: number;
  readonly distinct: number;
  /** Rendered strings, never numbers -- BIGINT/DECIMAL must stay exact. */
  readonly min: string | null;
  readonly max: string | null;
  readonly mean: number | null;
  readonly bins: readonly HistogramBin[];
  readonly top: readonly TopValue[];
}

/* -------------------------------------------------------------- SQL ------ */

export function summarySql(table: string, column: ColumnSchema): string {
  const col = quoteIdent(column.name);
  const cls = classifyType(column.type);
  const numeric = isNumericClass(cls);

  // min/max are cast to VARCHAR in SQL so wide integers never round-trip
  // through a JS number.
  return [
    'SELECT',
    '  count(*) AS total,',
    `  count(${col}) AS non_null,`,
    `  count(DISTINCT ${col}) AS distinct_count,`,
    `  CAST(min(${col}) AS VARCHAR) AS min_v,`,
    `  CAST(max(${col}) AS VARCHAR) AS max_v,`,
    numeric ? `  avg(CAST(${col} AS DOUBLE)) AS mean_v` : '  NULL AS mean_v',
    `FROM ${quoteIdent(table)}`,
  ].join('\n');
}

/** Numeric histogram over a fixed bin count, computed in one pass. */
export function histogramSql(table: string, column: ColumnSchema, bins = HISTOGRAM_BINS): string {
  const col = quoteIdent(column.name);
  const t = quoteIdent(table);
  const value = `CAST(${col} AS DOUBLE)`;
  return [
    'WITH bounds AS (',
    `  SELECT min(${value}) AS lo, max(${value}) AS hi FROM ${t} WHERE ${col} IS NOT NULL`,
    '), assigned AS (',
    '  SELECT least(',
    `    CAST(floor((${value} - bounds.lo) / nullif(bounds.hi - bounds.lo, 0) * ${bins}) AS BIGINT),`,
    `    ${bins - 1}`,
    '  ) AS bin, bounds.lo AS lo, bounds.hi AS hi',
    `  FROM ${t}, bounds WHERE ${col} IS NOT NULL`,
    ')',
    'SELECT coalesce(bin, 0) AS bin, count(*) AS n, any_value(lo) AS lo, any_value(hi) AS hi',
    'FROM assigned GROUP BY 1 ORDER BY 1',
  ].join('\n');
}

/** Temporal distribution: identical bucketing over epoch microseconds. */
export function timeseriesSql(table: string, column: ColumnSchema, bins = HISTOGRAM_BINS): string {
  const col = quoteIdent(column.name);
  const t = quoteIdent(table);
  const value = `CAST(epoch_ms(CAST(${col} AS TIMESTAMP)) AS DOUBLE)`;
  return [
    'WITH bounds AS (',
    `  SELECT min(${value}) AS lo, max(${value}) AS hi FROM ${t} WHERE ${col} IS NOT NULL`,
    '), assigned AS (',
    '  SELECT least(',
    `    CAST(floor((${value} - bounds.lo) / nullif(bounds.hi - bounds.lo, 0) * ${bins}) AS BIGINT),`,
    `    ${bins - 1}`,
    '  ) AS bin, bounds.lo AS lo, bounds.hi AS hi',
    `  FROM ${t}, bounds WHERE ${col} IS NOT NULL`,
    ')',
    'SELECT coalesce(bin, 0) AS bin, count(*) AS n, any_value(lo) AS lo, any_value(hi) AS hi',
    'FROM assigned GROUP BY 1 ORDER BY 1',
  ].join('\n');
}

export function topValuesSql(
  table: string,
  column: ColumnSchema,
  limit = TOP_VALUES_LIMIT,
): string {
  const col = quoteIdent(column.name);
  return [
    // The IS NULL flag is selected separately: casting NULL to VARCHAR yields
    // NULL, which is indistinguishable from a genuine 'NULL' string otherwise.
    `SELECT CAST(${col} AS VARCHAR) AS v, ${col} IS NULL AS is_null, count(*) AS n`,
    `FROM ${quoteIdent(table)}`,
    'GROUP BY 1, 2 ORDER BY n DESC, v ASC',
    `LIMIT ${Math.max(1, Math.trunc(limit))}`,
  ].join('\n');
}

/* ---------------------------------------------------------- execution ---- */

interface Row {
  readonly [key: string]: unknown;
}

function toRecords(columns: readonly ColumnSchema[], rows: readonly (readonly unknown[])[]): Row[] {
  return rows.map((row) => {
    const record: Record<string, unknown> = {};
    columns.forEach((column, index) => {
      record[column.name] = row[index];
    });
    return record;
  });
}

function formatBound(value: number, kind: DistributionKind): string {
  if (kind === 'timeseries') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '—' : date.toISOString().slice(0, 16).replace('T', ' ');
  }
  const abs = Math.abs(value);
  if (abs !== 0 && (abs >= 1e6 || abs < 1e-3)) return value.toExponential(2);
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}

export function distributionKindFor(type: DuckDbType, distinct: number): DistributionKind {
  const cls = classifyType(type);
  if (cls === 'boolean') return 'boolean';
  if (cls === 'temporal') return 'timeseries';
  if (cls === 'nested' || cls === 'binary') return 'none';
  if (isNumericClass(cls)) {
    // A numeric column with a handful of values is a category, not a distribution.
    return distinct > 0 && distinct <= LOW_CARDINALITY_THRESHOLD ? 'categories' : 'histogram';
  }
  return 'categories';
}

/** Runs the two or three statements a full profile needs. */
export async function profileColumn(
  engine: LocalEngine,
  table: string,
  column: ColumnSchema,
): Promise<ColumnProfile> {
  const summaryResult = await engine.query(summarySql(table, column));
  const summary = toRecords(summaryResult.columns, summaryResult.rows)[0] ?? {};

  const total = toCount(summary['total']);
  const nonNull = toCount(summary['non_null']);
  const distinct = toCount(summary['distinct_count']);
  const nullCount = Math.max(0, total - nonNull);
  const minRaw = summary['min_v'];
  const maxRaw = summary['max_v'];

  const kind = distributionKindFor(column.type, distinct);

  let bins: HistogramBin[] = [];
  let top: TopValue[] = [];

  if (nonNull > 0 && (kind === 'histogram' || kind === 'timeseries')) {
    const sql = kind === 'histogram' ? histogramSql(table, column) : timeseriesSql(table, column);
    const result = await engine.query(sql);
    const records = toRecords(result.columns, result.rows);

    const lo = toFiniteNumber(records[0]?.['lo']) ?? 0;
    const hi = toFiniteNumber(records[0]?.['hi']) ?? lo;
    const width = (hi - lo) / HISTOGRAM_BINS;

    const counts = new Array<number>(HISTOGRAM_BINS).fill(0);
    for (const record of records) {
      const index = toCount(record['bin']);
      if (index >= 0 && index < HISTOGRAM_BINS) counts[index] = toCount(record['n']);
    }

    bins = counts.map((count, index) => {
      const binLo = width === 0 ? lo : lo + width * index;
      const binHi = width === 0 ? hi : lo + width * (index + 1);
      return {
        index,
        lo: binLo,
        hi: binHi,
        count,
        label: `${formatBound(binLo, kind)} – ${formatBound(binHi, kind)}`,
      };
    });
  }

  // `none` covers LIST/STRUCT/MAP/BLOB. A GROUP BY over a JSON-text array column
  // is expensive and tells the user nothing, and patient_yearly has 559 of them,
  // so those columns get the summary only.
  if (kind === 'categories' || kind === 'boolean') {
    const result = await engine.query(topValuesSql(table, column));
    const records = toRecords(result.columns, result.rows);
    top = records.map((record) => {
      const isNull = record['is_null'] === true || record['is_null'] === 1;
      const raw = isNull ? null : renderableText(record['v']);
      const count = toCount(record['n']);
      return {
        value: raw,
        isNull,
        isEmpty: !isNull && raw === '',
        count,
        fraction: total > 0 ? count / total : 0,
      };
    });
  }

  return {
    column: column.name,
    type: column.type,
    kind,
    total,
    nonNull,
    nullCount,
    nullFraction: total > 0 ? nullCount / total : 0,
    distinct,
    min: minRaw === null || minRaw === undefined ? null : renderableText(minRaw),
    max: maxRaw === null || maxRaw === undefined ? null : renderableText(maxRaw),
    mean: toFiniteNumber(summary['mean_v']),
    bins,
    top,
  };
}

/**
 * Builds a WHERE fragment the user can paste into a query, from a chosen
 * bucket or top value. This is the "copy a range back into SQL" affordance.
 */
export function predicateForBin(column: string, bin: HistogramBin, kind: DistributionKind): string {
  const col = quoteIdent(column);
  if (kind === 'timeseries') {
    const lo = new Date(bin.lo).toISOString();
    const hi = new Date(bin.hi).toISOString();
    return `${col} >= TIMESTAMP ${sqlString(lo)} AND ${col} < TIMESTAMP ${sqlString(hi)}`;
  }
  return `${col} >= ${bin.lo} AND ${col} < ${bin.hi}`;
}

export function predicateForValue(column: string, value: TopValue): string {
  const col = quoteIdent(column);
  if (value.isNull) return `${col} IS NULL`;
  return `${col} = ${sqlString(value.value ?? '')}`;
}
